/**
 * Header behaviour: mobile drawer, desktop dropdowns and mega menus, the
 * search panel, and the sticky-on-scroll state.
 *
 * All of it is enhancement. Without JS the nav is still a list of links,
 * the drawer's contents are reachable, and search is a plain GET form.
 */
(function () {
  'use strict';

  var onceInit = (window.themeUtils && window.themeUtils.onceInit) || function (el, flag) {
    if (el.dataset[flag]) return false;
    el.dataset[flag] = 'true';
    return true;
  };

  var desktop = window.matchMedia('(min-width: 990px)');

  /* ------------------------------------------------------------- drawer -- */

  function initDrawer(root) {
    var drawer = document.querySelector('[data-header-drawer]');
    root.querySelectorAll('[data-drawer-toggle]').forEach(function (toggle) {
      if (!onceInit(toggle, 'drawerToggleInit')) return;
      toggle.addEventListener('click', function () {
        if (!drawer || typeof drawer.showModal !== 'function') return;
        if (drawer.open) {
          drawer.close();
        } else {
          drawer.showModal();
          toggle.setAttribute('aria-expanded', 'true');
        }
      });
    });

    if (drawer && onceInit(drawer, 'drawerCloseSync')) {
      drawer.addEventListener('close', function () {
        document.querySelectorAll('[data-drawer-toggle]').forEach(function (toggle) {
          toggle.setAttribute('aria-expanded', 'false');
        });
      });
    }
  }

  /* ------------------------------------------- dropdowns and mega menus -- */

  function initDisclosures(root) {
    var groups = root.querySelectorAll('[data-dropdown], [data-mega-menu]');

    groups.forEach(function (group) {
      if (!onceInit(group, 'disclosureInit')) return;

      var toggle = group.querySelector('[data-dropdown-toggle], [data-mega-toggle]');
      var panel = group.querySelector('.header__dropdown, [data-mega-panel]');
      if (!toggle || !panel) return;

      var hoverTimer = null;

      function open() {
        closeOthers();
        panel.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        group.classList.add('is-open');
      }

      function close() {
        panel.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
        group.classList.remove('is-open');
      }

      function closeOthers() {
        groups.forEach(function (other) {
          if (other === group) return;
          var otherToggle = other.querySelector('[data-dropdown-toggle], [data-mega-toggle]');
          var otherPanel = other.querySelector('.header__dropdown, [data-mega-panel]');
          if (otherPanel) otherPanel.hidden = true;
          if (otherToggle) otherToggle.setAttribute('aria-expanded', 'false');
          other.classList.remove('is-open');
        });
      }

      group._close = close;

      toggle.addEventListener('click', function () {
        if (panel.hidden) open();
        else close();
      });

      /* Pointer hover opens on desktop only; touch devices report no hover
         and keep the click behaviour, which is what makes the same markup
         work on a laptop and a tablet. */
      group.addEventListener('pointerenter', function (event) {
        if (event.pointerType === 'touch' || !desktop.matches) return;
        clearTimeout(hoverTimer);
        open();
      });

      group.addEventListener('pointerleave', function (event) {
        if (event.pointerType === 'touch' || !desktop.matches) return;
        hoverTimer = setTimeout(close, 140);
      });

      group.addEventListener('focusout', function (event) {
        if (!group.contains(event.relatedTarget)) close();
      });

      group.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && !panel.hidden) {
          close();
          toggle.focus();
        }
      });
    });

    if (onceInit(document.documentElement, 'disclosureOutsideInit')) {
      document.addEventListener('click', function (event) {
        document.querySelectorAll('[data-dropdown].is-open, [data-mega-menu].is-open').forEach(function (group) {
          if (!group.contains(event.target) && group._close) group._close();
        });
      });
    }
  }

  /* ------------------------------------------------------ search panel -- */

  function initSearch(root) {
    var panel = document.querySelector('[data-search-panel]');
    if (!panel) return;

    root.querySelectorAll('[data-search-toggle]').forEach(function (toggle) {
      if (!onceInit(toggle, 'searchToggleInit')) return;
      toggle.addEventListener('click', function () {
        var willOpen = panel.hidden;
        panel.hidden = !willOpen;
        toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        if (willOpen) {
          var input = panel.querySelector('[data-predictive-search-input]');
          if (input) input.focus();
        }
      });
    });

    if (onceInit(panel, 'searchPanelInit')) {
      panel.querySelectorAll('[data-search-close]').forEach(function (button) {
        button.addEventListener('click', closeSearch);
      });
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && !panel.hidden) closeSearch();
      });
      document.addEventListener('click', function (event) {
        if (panel.hidden) return;
        if (panel.contains(event.target) || event.target.closest('[data-search-toggle]')) return;
        closeSearch();
      });
    }

    function closeSearch() {
      panel.hidden = true;
      document.querySelectorAll('[data-search-toggle]').forEach(function (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
        toggle.focus();
      });
    }
  }

  /* ------------------------------------------------------- locale form -- */

  function initLocale(root) {
    root.querySelectorAll('[data-locale-select]').forEach(function (select) {
      if (!onceInit(select, 'localeInit')) return;
      select.addEventListener('change', function () {
        var form = select.closest('form');
        if (form) form.submit();
      });
    });
  }

  /* --------------------------------------------------------- scrolled --- */

  function initScrollState() {
    var header = document.querySelector('[data-header]');
    if (!header || !onceInit(header, 'scrollStateInit')) return;

    var lastY = window.scrollY;

    function update() {
      var y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      lastY = y;
    }

    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  function init(root) {
    initDrawer(root);
    initDisclosures(root);
    initSearch(root);
    initLocale(root);
    initScrollState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
